import netlifyIdentity from 'netlify-identity-widget'
import { error, getDebugLogger } from '../../utils/logger'

const DEBUG_PREFIX = 'AuthService'
const MS_IN_SEC = 1000
const TOKEN_EARLY_UPDATE = 5 * 60 * MS_IN_SEC // Token refresh is triggered 5 minutes before expiration

let refreshIntervalId: NodeJS.Timeout | null = null

interface INetlifyIdentity {
  id: string
  email: string
  user_metadata: {
    full_name: string
  }
  app_metadata: {
    roles?: string[]
  }
  token: {
    access_token: string
    expires_at: number
    expires_in: number
  }
}

interface IUserdata {
  id: string
  email: string
  name: string
  plan: string
  stripeEnabled: boolean
  accessToken: string
  isTokenExpired: boolean
}

let currentIdentity: INetlifyIdentity | null = null

function setCurrentIdentity(identity: INetlifyIdentity) {
  currentIdentity = identity
  publishNewUserdata(formatIdentity(identity))
}

const debug = getDebugLogger(DEBUG_PREFIX)

debug('module')

/* Initial auth token */

// TODO: implement a promise which returns the initial auth token for rendering the app
//       this must ensure that the token is renewed if it's expired

/* Broadcasting */

type IUserdataHandler = (token: IUserdata) => void

const subscribers = new Set<IUserdataHandler>()

function invokeAsync(cb: () => void) {
  setTimeout(cb, 0)
}

function subscribeToUserdata(handler: IUserdataHandler) {
  subscribers.add(handler)
  invokeAsync(() => {
    if (currentIdentity != null && !isTokenExpired(currentIdentity)) {
      handler(formatIdentity(currentIdentity)) // Trigger the handler if token is valid
    }
  })
}

function unsubscribeFromUserdata(handler: IUserdataHandler) {
  subscribers.delete(handler)
}

function publishNewUserdata(userdata: IUserdata) {
  for (const subscriber of subscribers) {
    invokeAsync(() => {
      subscriber(userdata)
    })
  }
}

/* Entry point */
console.log('going into neflify entrypoint', typeof window !== `undefined` && typeof document !== `undefined`)
// TODO: remove debug event handlers and console output
if (typeof window !== `undefined` && typeof document !== `undefined`) {  // eslint-disable-line no-restricted-globals
  console.log('running netlify identity widget')  // eslint-disable-line no-console
  window.netlifyIdentity = netlifyIdentity
  debug('init', { currentIdentity: netlifyIdentity.currentUser() })
  netlifyIdentity.on('init', initialize)
  netlifyIdentity.on('login', user => debug('identity login', { user }))
  netlifyIdentity.on('logout', () => debug('identity logged out'))
  netlifyIdentity.on('error', err => error('identity error', { err }))
  netlifyIdentity.on('open', () => debug('identity widget opened'))
  netlifyIdentity.on('close', () => debug('identity widget closed'))
  netlifyIdentity.init({
    APIUrl: 'https://adorable-basbousa-abea15.netlify.app/.netlify/identity',
  })
}

function redirectToLogin() {
  if (typeof window !== `undefined`) {  // eslint-disable-line no-restricted-globals
    const currentPath = window.location.pathname  // eslint-disable-line no-restricted-globals

    // Check if we're already on login page (handle both /login and /app/login cases)
    if (currentPath.includes('login')) {
      debug('Already on login page, not redirecting', { currentPath })
      return
    }

    // Determine the correct path prefix
    const pathPrefix = currentPath.includes('/app/') ? '/app' : ''
    const loginUrl = `${window.location.origin}${pathPrefix}/login`  // eslint-disable-line no-restricted-globals

    debug(`Redirecting to login page`, { currentPath, loginUrl, GATSBY_PREFIX: process.env.GATSBY_PREFIX })
    window.location.href = loginUrl  // eslint-disable-line no-restricted-globals
  }
}

function initialize(identity: INetlifyIdentity) {
  debug('identity initialize', { identity, currentIdentity })
  if (identity == null) {
    redirectToLogin()
    return
  }

  setCurrentIdentity(identity)
  scheduleRecurringRefreshToken()
}

function isTokenExpired(identity: INetlifyIdentity | null): boolean {
  const timeNow = Date.now()
  if (identity == null) {
    return true
  }
  return (identity.token.expires_at - timeNow) < 0
}

function getNextUpdateIn(identity: INetlifyIdentity): number {
  const timeNow = Date.now()
  return (identity!.token.expires_at - timeNow - TOKEN_EARLY_UPDATE)
}

async function getAccessToken(): Promise<string> {
  debug('getAccessToken', { isTokenExpired: isTokenExpired(currentIdentity) })
  if (isTokenExpired(currentIdentity)) {
    await refreshToken()
  }
  return currentIdentity!.token.access_token
}

async function refreshToken() {
  return netlifyIdentity.refresh()
    .then(jwt => {
      debug('refreshToken interval: the token is updated', { jwt })
      const identity = netlifyIdentity.currentUser()
      setCurrentIdentity(identity)
    })
    .catch(error => { // TODO: handle errors during token refresh
      debug('refreshToken interval: error', { error })
      logout()
      redirectToLogin()
    })
}

function scheduleRecurringRefreshToken() {
  // Clear any existing interval to prevent duplicates
  if (refreshIntervalId !== null) {
    clearInterval(refreshIntervalId)
    refreshIntervalId = null
  }

  // Calculate refresh interval (token duration minus 5 minute buffer)
  const refreshInterval = currentIdentity!.token.expires_in * MS_IN_SEC - TOKEN_EARLY_UPDATE
  const nextUpdateIn = getNextUpdateIn(currentIdentity!)

  debug('scheduleRecurringRefreshToken', {
    nextUpdateIn,
    refreshInterval,
    expiresIn: currentIdentity!.token.expires_in,
    expiresAt: new Date(currentIdentity!.token.expires_at)
  })

  // Set up a single recurring interval that will refresh the token periodically
  // The interval is set to the refresh interval, not the full expiration time
  refreshIntervalId = setInterval(() => {
    debug('refreshToken interval: refreshing the token...', {
      currentIdentity,
      timeUntilExpiry: currentIdentity?.token.expires_at ? currentIdentity.token.expires_at - Date.now() : 'unknown'
    })
    refreshToken()
  }, refreshInterval)

  // If the token is already close to expiring, refresh it immediately
  if (nextUpdateIn <= 0) {
    debug('Token is already expired or about to expire, refreshing immediately')
    refreshToken()
  }
}

function logout() {
  debug('logout: clearing all auth data')

  // Clear the refresh interval
  if (refreshIntervalId !== null) {
    clearInterval(refreshIntervalId)
    refreshIntervalId = null
  }

  // Clear current identity
  currentIdentity = null

  // Clear all localStorage items related to auth
  if (typeof window !== 'undefined' && window.localStorage) {
    // Clear Netlify Identity related items
    localStorage.removeItem('gotrue.user')
    localStorage.removeItem('netlify-identity-token')

    // Clear any items that start with 'gotrue.' or 'netlify'
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && (key.startsWith('gotrue.') || key.startsWith('netlify'))) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key))
  }

  // Clear all cookies (if any)
  if (typeof document !== 'undefined') {
    // Clear cookies by setting them to expire in the past
    document.cookie.split(";").forEach(function(c) {
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/")
    })
  }

  // Clear sessionStorage as well
  if (typeof window !== 'undefined' && window.sessionStorage) {
    sessionStorage.clear()
  }

  // Call Netlify Identity logout
  netlifyIdentity.logout()

  // Navigate to root and force reload to clear any cached state
  if (typeof window !== 'undefined') {
    // Determine the correct path prefix for redirect
    const currentPath = window.location.pathname
    const pathPrefix = currentPath.includes('/app/') ? '/app' : ''
    const rootUrl = pathPrefix ? `${window.location.origin}${pathPrefix}/` : '/'

    debug('logout: redirecting to root', { currentPath, rootUrl })

    setTimeout(() => {
      window.location.href = rootUrl
    }, 100)
  }
}

function formatIdentity(identity: INetlifyIdentity): IUserdata {
  return {
    id: identity.id,
    email: identity.email,
    name: identity.user_metadata.full_name,
    plan: identity.app_metadata.roles?.includes('premium') ? 'premium' : 'free',
    stripeEnabled: identity.app_metadata.roles?.includes('stripe') ?? false,
    accessToken: identity.token.access_token,
    isTokenExpired: isTokenExpired(identity), // Use actual expiration check, not early update buffer
  }
}

export {
  IUserdata, getAccessToken,
  logout, subscribeToUserdata,
  unsubscribeFromUserdata
}
